import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../router/authContext";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import './css/header.css'; // We'll create this for video styles

export const Header = (props) => {
  const [headerRef, isHeaderVisible] = useScrollAnimation({ threshold: 0.3, triggerOnce: true });
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const video1Ref = useRef(null);
  const video2Ref = useRef(null);

  const {user, isAuthenticated} = useAuth();

  const videos = [
    "/videos/traffic-1.mp4",
    "/videos/traffic-2.mp4"
  ];

  const getCurrentVideoRef = () => currentVideoIndex % 2 === 0 ? video1Ref : video2Ref;
  const getNextVideoRef = () => currentVideoIndex % 2 === 0 ? video2Ref : video1Ref;

  const handleVideoEnd = () => {
    const nextIndex = (currentVideoIndex + 1) % videos.length;
    const nextVideoRef = getNextVideoRef();
    
    // Prepare the next video
    if (nextVideoRef.current) {
      nextVideoRef.current.src = videos[nextIndex];
      nextVideoRef.current.load();
      nextVideoRef.current.currentTime = 0;
    }

    // Start transition
    setIsTransitioning(true);
    
    // Start playing the next video
    setTimeout(() => {
      if (nextVideoRef.current) {
        nextVideoRef.current.play();
      }
    }, 100);

    // Complete transition after fade duration
    setTimeout(() => {
      setCurrentVideoIndex(nextIndex);
      setIsTransitioning(false);
      
      // Pause the previous video to save resources
      const currentVideoRef = getCurrentVideoRef();
      if (currentVideoRef.current) {
        currentVideoRef.current.pause();
      }
    }, 800); // Match the CSS transition duration
  };

  useEffect(() => {
    // Initialize first video
    if (video1Ref.current) {
      video1Ref.current.src = videos[0];
      video1Ref.current.load();
    }
    
    // Preload second video
    if (video2Ref.current) {
      video2Ref.current.src = videos[1] || videos[0];
      video2Ref.current.load();
    }
  }, []);

  return (
    <div className="sm:px-6 lg:px-8 py-2 mt-12 relative">
      <div 
        ref={headerRef}
        className={`p-12 lg:p-20 transition-all duration-1000 ease-out transform relative overflow-hidden ${
          isHeaderVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Video Container - Absolutely positioned to right half */}
        <div className="video-container-right">
          {/* Video 1 */}
          <video
            ref={video1Ref}
            autoPlay
            muted
            playsInline
            className={`video-background-right ${
              currentVideoIndex % 2 === 0 
                ? (isTransitioning ? 'video-fade-out' : 'video-active') 
                : (isTransitioning ? 'video-fade-in' : 'video-inactive')
            }`}
            onEnded={handleVideoEnd}
          >
            <source type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* Video 2 */}
          <video
            ref={video2Ref}
            muted
            playsInline
            className={`video-background-right ${
              currentVideoIndex % 2 === 1 
                ? (isTransitioning ? 'video-fade-out' : 'video-active') 
                : (isTransitioning ? 'video-fade-in' : 'video-inactive')
            }`}
            onEnded={handleVideoEnd}
          >
            <source type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          <div className="video-overlay-right"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            {/* Left Content - Text */}
            <div className="w-full md:w-1/2 text-center md:text-left">
              <h1 className="font-nunito text-4xl md:text-6xl font-bold text-black mb-4 leading-tight">
                Planning today. Powering tomorrow.
              </h1>
              <p className="text-gray-800 text-2xl mb-8 leading-relaxed font-nunito">
                Analytics and strategic solutions to make every mile smarter.
              </p>
              
              {/* Call-to-action Button */}
              <div className="pt-4">
                {isAuthenticated ? (
                  <Link 
                    to="/profile" 
                    className="btn-primary btn-lg"
                  >
                  Get Started
                  </Link>
                ) : (
                  <Link 
                    to="/login?mode=register" 
                    className="btn-primary btn-lg"
                  >
                    Get Started
                  </Link>
                )}
              </div>
            </div>
            
            {/* Right Content - Empty space for video */}
            <div className="w-full md:w-1/2">
              {/* Video is absolutely positioned, this div maintains layout */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
